'use client';

import posthog from 'posthog-js';
import { PostHogProvider as PHProvider } from 'posthog-js/react';
import { useEffect, useRef, useState } from 'react';
import { createClient } from '@/utils/supabase/client';
import { analytics } from '@/lib/analytics';
import { initPostHog } from '@/lib/posthog-browser';
import { fileTiming } from '@/lib/file-timing';
import PageView from '@/components/tracking/PageView';

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  const [supabase] = useState(() => createClient());
  // Dedupe SIGNED_IN: Supabase fires it on the initial sign-in but can also
  // re-fire on token refresh, tab focus, and hydration. Only emit when the
  // user identity actually changes.
  const lastUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    initPostHog();
    fileTiming.start();
  }, []);

  // Source auth analytics from Supabase's onAuthStateChange so server-driven
  // events (token-refresh failure, admin-revoked session, "log out everywhere"
  // from another device) get captured alongside explicit user actions.
  // Per-tab dedup via lastUserIdRef collapses Supabase's same-id re-fires
  // (token refresh, tab focus, hydration). The sessionStorage transition
  // catches the no-JS sign-out fallback: the server action clears cookies and
  // redirects to a fresh page where INITIAL_SESSION fires with no user but
  // sessionStorage still holds the prior id.
  //
  // Trade-off accepted: Supabase broadcasts state to every open tab, so N tabs
  // see N events per real action. We prefer over-capture (analytics noise) to
  // under-capture (missing involuntary sign-outs).
  useEffect(() => {
    const LAST_USER_KEY = 'tone3000:last_user_id';
    const readPriorUserId = () =>
      typeof window === 'undefined' ? null : sessionStorage.getItem(LAST_USER_KEY);
    const writePriorUserId = (id: string) => {
      if (typeof window !== 'undefined') sessionStorage.setItem(LAST_USER_KEY, id);
    };
    const clearPriorUserId = () => {
      if (typeof window !== 'undefined') sessionStorage.removeItem(LAST_USER_KEY);
    };

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      switch (event) {
        case 'INITIAL_SESSION':
          if (session?.user) {
            lastUserIdRef.current = session.user.id;
            writePriorUserId(session.user.id);
            analytics.identify(session.user.id, { email: session.user.email });
            analytics.capture('auth.session.initial');
          } else if (readPriorUserId()) {
            clearPriorUserId();
            lastUserIdRef.current = null;
            analytics.capture('auth.signed_out', { source: 'auth_state_change' });
            analytics.reset();
          }
          break;
        case 'SIGNED_IN':
          if (session?.user && session.user.id !== lastUserIdRef.current) {
            lastUserIdRef.current = session.user.id;
            writePriorUserId(session.user.id);
            analytics.identify(session.user.id, { email: session.user.email });
            analytics.capture('auth.signed_in', { source: 'auth_state_change' });
          }
          break;
        case 'SIGNED_OUT':
          lastUserIdRef.current = null;
          clearPriorUserId();
          analytics.capture('auth.signed_out', { source: 'auth_state_change' });
          analytics.reset();
          break;
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [supabase]);

  return (
    <PHProvider client={posthog}>
      <PageView />
      {children}
    </PHProvider>
  );
}
